import { Badge } from '@/components/ui/Badge';
import {
  dueState,
  getEquipmentConditionMeta,
  getEquipmentStatusMeta,
  warrantyState,
} from '@/features/equipment/model';
import { formatDateOnly } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function EquipmentStatusBadge({ status }: { status: string }) {
  const meta = getEquipmentStatusMeta(status);
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export function EquipmentConditionBadge({ condition }: { condition: string | null }) {
  const meta = getEquipmentConditionMeta(condition);
  if (!meta) return <span className="text-muted-foreground">—</span>;
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

/** Next-due date with an overdue / due-soon tint. */
export function DueDate({ date, className }: { date: string | null; className?: string }) {
  const state = dueState(date);
  if (state === 'none') return <span className={cn('text-muted-foreground', className)}>—</span>;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums',
        state === 'overdue' && 'font-medium text-fresh-salmon-dark',
        state === 'due_soon' && 'text-atmosphere-dark',
        className,
      )}
    >
      {formatDateOnly(date)}
      {state === 'overdue' && <Badge variant="warning">Overdue</Badge>}
      {state === 'due_soon' && <Badge variant="info-soft">Due soon</Badge>}
    </span>
  );
}

export function WarrantyBadge({ expiresOn }: { expiresOn: string | null }) {
  const state = warrantyState(expiresOn);
  if (state === 'none') return <span className="text-muted-foreground">—</span>;
  if (state === 'expired') return <Badge variant="muted">Expired {formatDateOnly(expiresOn)}</Badge>;
  if (state === 'expiring') {
    return <Badge variant="warning-soft">Ends {formatDateOnly(expiresOn)}</Badge>;
  }
  return <Badge variant="success-soft">Until {formatDateOnly(expiresOn)}</Badge>;
}
